import { useContext, useEffect, useState } from 'react';
import { ImagenOptimizada } from 'shared-components/Components/Image/_new_web';
import { PriceTag } from 'shared-components/Components/Property/PriceTag/web';
import { Title } from 'shared-components/Components/Property/Title/web';
import { Favorite } from 'shared-components/Components/Property/Favorite/web';
import { LeadButtons } from 'shared-components/Components/Property/InformationRequest/LeadButtons/web';
import { formatMoney, showBathrooms, showBedrooms, showGuests, showM2, showRooms } from '@Utils/Functions';
import { MapCardSkeleton } from './MapCardSkeleton';
import { useWindowSize } from 'shared-components/GlobalHooks/useWindowSize';
import { fetchSearchResults } from 'src/services/search';
import { ConfigStateContext } from 'shared-components/Contexts/Configurations/context';
import { Listing } from 'shared-components';
import { CardLink } from 'shared-components/ViewFragments/SearchPage/ListingCard';
import { firstLetterToUppercase, getProjectRangePrices, getProjectUnitsMinValues } from "src/utils/Functions";

interface MapCardProps {
    data: any,
    isActive: boolean,
    index?: number,
    currentIndex?: number,
    nextPreviousCallback?: (type, index) => void
}

export const MapCard = ({data, isActive, index, currentIndex, nextPreviousCallback} : MapCardProps) => {
	const { main_domain, country_code, translations } = useContext(ConfigStateContext);
    const screen = useWindowSize();
	const [loading, setLoading] = useState(true);
    const [property, setPorperty] = useState({} as Listing);
    // El panel lateral solo existe desde xl (1200px); debajo va el carrusel inferior
    const isDesktop = screen.xl;
    const ROOMS_PROPERTY_TYPES = [4, 5, 9, 19];

    useEffect(() => {
		setLoading(true);

		const payload = {
			rows: 1,
			params: {
				id: data.id,
			},
			page: 1,
			source: 10,
		};

		fetchSearchResults(payload, main_domain)
			.then(res => {
				setPorperty(res.property);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	}, [data.id]);

    const handleCallback = () => {
        if (nextPreviousCallback && isDesktop) {
            const type = index > currentIndex ? "next" : "";

            nextPreviousCallback(type, index)
        }
    }

    const useRooms = ROOMS_PROPERTY_TYPES.includes(Number(property.property_type?.id));

    // misma lógica que ListingCard: en proyectos se muestra el menor valor entre todas
    // las unidades y el precio de la unidad más barata, sin importar los filtros
    const isProjectListing = !!property.isProject;
    const {
        minBedrooms: minUnitsBedrooms,
        minRooms: minUnitsRooms,
        minBathrooms: minUnitsBathrooms,
        minM2: minUnitsM2,
    } = getProjectUnitsMinValues({ listing: property, country_code });
    const projectPrices = isProjectListing ? getProjectRangePrices({ listing: property }) : null;

    const dataBedroom = isProjectListing ? minUnitsBedrooms : property.bedrooms;
    const dataRooms = isProjectListing ? minUnitsRooms : property.rooms;
    const dataBathroom = isProjectListing ? minUnitsBathrooms : property.bathrooms;
    const dataM2 = isProjectListing && minUnitsM2 !== null ? minUnitsM2 : property.m2;

    const getBedroomsText = (bedroomsValue) =>
        bedroomsValue === 0 && country_code !== "CO"
            ? "Mono"
            : bedroomsValue + (bedroomsValue > 1
                ? ` ${firstLetterToUppercase(translations.habs)}`
                : ` ${firstLetterToUppercase(translations.hab)}`);

    const renderTypologySpaces = ()=> {
        if (isProjectListing) {
            // se prioriza bedrooms; rooms solo si ninguna unidad aporta bedrooms
            if (dataBedroom !== null && typeof dataBedroom !== "undefined") {
                return (
                    <span className="typology-bedrooms">
                        <span>{getBedroomsText(dataBedroom)}</span>
                    </span>
                )
            }
            if (dataRooms !== null && typeof dataRooms !== "undefined") {
                return (
                    <span className="typology-rooms">
                        <span>{dataRooms == 1
                            ? dataRooms + " Ambiente"
                            : dataRooms + " Ambientes"}</span>
                    </span>
                )
            }
            return <></>
        }
        if (useRooms && showRooms([Number(property.property_type.id)], country_code, property.rooms)) {
            return (
                (
                    <span className="typology-rooms">
                        <span>{property.rooms == 1
                            ? property.rooms + " Ambiente"
                            : property.rooms + " Ambientes"}</span>
                    </span>
                )
            )

        }
        else if (!useRooms && showBedrooms([Number(property.property_type.id)], Number(property.operation_type.id), country_code)) {
            return (
                <span className="typology-bedrooms">
                    <span>{getBedroomsText(property.bedrooms)}</span>
                </span>
            )
        }
        return <></>
    }

    const hideBathrooms = (!property.isProject && property.bathrooms === 0) ?? false
    const shouldShowBathroomsTag = isProjectListing
        ? dataBathroom !== null
        : showBathrooms([Number(property.property_type?.id)], country_code, property.bathrooms) && !hideBathrooms;

    return (
        <>
            {(loading && typeof property.id === "undefined") ? <MapCardSkeleton />
            :(
                <div
                    className={`map-card ${isActive ? "map-card--active" : ""}`}
                    onMouseEnter={handleCallback}
                >
                    <CardLink
                        data={property}
                        onCancel={handleCallback}
                        className='map-card--link'
                        isActive={isActive || !isDesktop}
                    >
                        <div className='map-card__gallery'>
                            <ImagenOptimizada src={property.images?.[0]?.image} alt="Image" width={300} height={270} />
                        </div>

                        <div className='map-card__content'>
                            <div className="property-card-top">
                                <Favorite id={property.id} type={"justIcon"} />
                            </div>

                            <PriceTag
                                price={
                                    isProjectListing && projectPrices?.minPrice
                                        ? { ...projectPrices.minPrice, hidePrice: false }
                                        : property.price
                                }
                                operationType={property.operation_type}
                                temporalCurrency={property.temporal_currency}
                                temporalPrice={property.temporal_price}
                                commonExpenses={property.commonExpenses}
                                isProject={isProjectListing}
                            />

                            <Title title={property.title} hideInformation limitLength={1} level={5} />

                            <div className="lc-typologyTag">
                                {isProjectListing && Array.isArray(property.commercial_units) && property.commercial_units.length > 1 && (
                                    <span className="typology-units-from">
                                        <span>Unidades desde:</span>
                                    </span>
                                )}
                                {renderTypologySpaces()}
                                {shouldShowBathroomsTag && (
                                    <span className="typology-bathrooms">
                                        <span>{dataBathroom} {dataBathroom > 1 ? "Baños" : "Baño"}</span>
                                    </span>
                                )}
                                {showM2(Number(property.operation_type.id)) && dataM2 > 0 && (
                                    <span className="typology-m2">
                                        <span>{formatMoney(dataM2)} m²</span>
                                    </span>
                                )}
                                {showGuests(Number(property.operation_type.id)) && property.guests && (
                                    <span className="typology-guests">
                                        <span>{property.guests} Huéspedes</span>
                                    </span>
                                )}
                            </div>

                            <LeadButtons
                                id={property.id}
                                owner={property.owner}
								seller={property.seller}
                                propertyLink={property?.link}
                                isInListing
                                source="card"
                                ctaToShow={{
                                    contact: true,
                                    phone: true,
                                    whatsapp: true
                                }}
                            />
                        </div>
                    </CardLink>
                </div>
            )}
        </>
    )
}
